/**
 * Operator console API client. Same cookie/CSRF rules as the org console.
 *
 * Session is an HttpOnly cookie. A 401 triggers one refresh attempt
 * (`POST /auth/refresh`) so an expired access token does not bounce staff
 * back to the sign-in screen while a valid refresh cookie is still live.
 */
import { deviceHeaders } from "./device";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

export const SESSION_EXPIRED_EVENT = "sales-admin:session-expired";

export function notifySessionExpired(code?: string | null): void {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { code: code ?? null } }));
}

function shouldBroadcastExpiry(path: string): boolean {
  return path !== "/auth/session" && path !== "/auth/logout" && shouldRefreshOnUnauthorized(path);
}

function pageHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const page = `${window.location.pathname}${window.location.search}`.slice(0, 200);
  if (!page.startsWith("/")) return {};
  return { "X-Saleslox-Page": page };
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  code: string | null;

  constructor(status: number, message: string, body: unknown = null, code: string | null = null) {
    super(message);
    this.status = status;
    this.body = body;
    this.code = code;
  }
}

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function looksTechnical(message: string): boolean {
  const lower = message.toLowerCase();
  if (message.length > 180) return true;
  if (lower.includes("traceback") || lower.includes("<html") || lower.includes("<!doctype")) return true;
  if (/file ".*", line \d+/i.test(message)) return true;
  const trimmed = message.trim();
  if ((trimmed.startsWith("{") || trimmed.startsWith("[")) && trimmed.length > 40) return true;
  return false;
}

function errorMessage(payload: unknown, fallback: string): string {
  const body = payload as { detail?: unknown; message?: string } | null;
  if (!body) return fallback;
  let raw = "";
  if (typeof body.detail === "string" && body.detail) raw = body.detail;
  else if (Array.isArray(body.detail)) {
    const msgs = body.detail
      .map((item) => {
        if (!item || typeof item !== "object" || !("msg" in item)) return "";
        return String((item as { msg: unknown }).msg || "").trim();
      })
      .filter(Boolean);
    if (msgs.length) raw = msgs.join(" ");
  } else if (body.detail && typeof body.detail === "object" && "message" in body.detail) {
    const nested = (body.detail as { message?: unknown }).message;
    if (typeof nested === "string" && nested) raw = nested;
  } else if (body.message) {
    raw = body.message;
  }
  if (!raw) return fallback;
  if (looksTechnical(raw)) return fallback;
  return raw;
}

function clientFallback(status: number, statusText: string): string {
  if (status >= 500) return "Something went wrong. Try again.";
  return statusText || "Request failed";
}

function errorCode(payload: unknown): string | null {
  const body = payload as { detail?: unknown; code?: unknown } | null;
  if (body?.detail && typeof body.detail === "object" && body.detail && "code" in body.detail) {
    const nested = (body.detail as { code?: unknown }).code;
    return typeof nested === "string" ? nested : null;
  }
  return typeof body?.code === "string" ? body.code : null;
}

function shouldRefreshOnUnauthorized(path: string): boolean {
  return path !== "/auth/login" && path !== "/auth/refresh";
}

let refreshInFlight: Promise<boolean> | null = null;
let authGeneration = 0;
let refreshHeld = 0;

/** See the org console client: a sign-in must not refresh the session it just replaced. */
export function holdSessionRefresh(): () => void {
  authGeneration += 1;
  refreshHeld += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    refreshHeld = Math.max(0, refreshHeld - 1);
    authGeneration += 1;
  };
}

async function tryRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: {
        Origin: window.location.origin,
        "X-Client": "saleslox-admin",
        ...deviceHeaders(),
        ...pageHeaders(),
      },
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/**
 * Identical mutations (same method, path and body) that are still in flight share one request,
 * so a double click reaches the API once. Sequential repeats are unaffected.
 */
const mutationsInFlight = new Map<string, Promise<unknown>>();

function request<T>(
  path: string,
  method = "GET",
  body?: unknown,
  extraOkStatuses: number[] = [],
  isRetry = false,
): Promise<T> {
  if (!MUTATING.has(method) || isRetry) return send<T>(path, method, body, extraOkStatuses, isRetry);
  const key = `${method} ${path} ${body === undefined ? "" : JSON.stringify(body)}`;
  const existing = mutationsInFlight.get(key);
  if (existing) return existing as Promise<T>;
  const tracked = send<T>(path, method, body, extraOkStatuses, isRetry).finally(() => {
    if (mutationsInFlight.get(key) === tracked) mutationsInFlight.delete(key);
  });
  mutationsInFlight.set(key, tracked);
  return tracked;
}

async function send<T>(
  path: string,
  method = "GET",
  body?: unknown,
  extraOkStatuses: number[] = [],
  isRetry = false,
): Promise<T> {
  const generation = authGeneration;
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Client": "saleslox-admin",
    ...deviceHeaders(),
    ...pageHeaders(),
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (MUTATING.has(method)) headers["Origin"] = window.location.origin;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: "include",
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const payload: unknown = (res.headers.get("content-type") ?? "").includes("application/json")
    ? await res.json().catch(() => null)
    : null;
  const code = errorCode(payload);
  if (res.status === 401 && !isRetry && shouldRefreshOnUnauthorized(path)) {
    if (generation !== authGeneration || refreshHeld > 0) {
      throw new ApiError(401, errorMessage(payload, "Not authenticated"), payload, code);
    }
    if (code !== "session_replaced") {
      const refreshed = await tryRefresh();
      if (refreshed) return send<T>(path, method, body, extraOkStatuses, true);
    }
    if (shouldBroadcastExpiry(path)) notifySessionExpired(code);
    throw new ApiError(401, errorMessage(payload, "Not authenticated"), payload, code);
  }
  if (!res.ok && !extraOkStatuses.includes(res.status)) {
    throw new ApiError(res.status, errorMessage(payload, clientFallback(res.status, res.statusText)), payload, code);
  }
  return payload as T;
}

async function uploadFile<T>(path: string, file: File, isRetry = false): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    Origin: window.location.origin,
    ...deviceHeaders(),
    ...pageHeaders(),
  };
  const body = new FormData();
  body.append("file", file);
  const res = await fetch(`${API_BASE_URL}${path}`, { method: "POST", credentials: "include", headers, body });
  const payload: unknown = (res.headers.get("content-type") ?? "").includes("application/json")
    ? await res.json().catch(() => null)
    : null;
  const code = errorCode(payload);
  if (res.status === 401 && !isRetry && shouldRefreshOnUnauthorized(path)) {
    if (code !== "session_replaced" && (await tryRefresh())) return uploadFile<T>(path, file, true);
    if (shouldBroadcastExpiry(path)) notifySessionExpired(code);
    throw new ApiError(401, errorMessage(payload, "Not authenticated"), payload, code);
  }
  if (!res.ok) {
    throw new ApiError(res.status, errorMessage(payload, clientFallback(res.status, res.statusText)), payload, code);
  }
  return payload as T;
}

export const api = {
  upload: <T>(path: string, file: File) => uploadFile<T>(path, file),
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, "POST", body),
  postAllowStatus: <T>(path: string, body: unknown, extraOkStatuses: number[]) =>
    request<T>(path, "POST", body, extraOkStatuses),
  put: <T>(path: string, body?: unknown) => request<T>(path, "PUT", body),
  patch: <T>(path: string, body?: unknown) => request<T>(path, "PATCH", body),
  delete: <T>(path: string) => request<T>(path, "DELETE"),
};
